import React, { useContext, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import { AppContext, SnackContext } from 'src/store/ContextStore';
import { DEFAULT_CANVAS_PREFERENCES } from './pedigree-utils';
import { isAnonymousOrLoading, clampLabelsSet } from './anonymousPedigrees';
import { useLabelPresets } from './label-modal/useLabelPresets';
import axios from 'src/axios';
import { APICONSTANTS, PEDIGREE } from '../Constants';
import { EVENTS } from '../admin/appReducer';

import Pedigree from './pedigree-tool/PedigreeChart';
import { pedigreeConfigurationFrom } from './pedigreeConfigurationState';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const Container = styled(Box)({
  width: '100%',
  position: 'relative'
});

export default function PedigreeBox({
  patient,
  isLocked,
  // TGCLOUD-1813: pass-through to the chart — whether the family lock has been
  // read yet. See PedigreeChartView.
  lockResolved = false,
  isMine,
  lockState,
  requestStatus,
  onRequestLock,
  onCancelRequest,
  onForceAcquireLock,
  pendingRequest,
  onRespondToRequest,
  personLockHook,
  onEdit,
  preview = false,
  // TGCLOUD-1669 Phase 7: pass-through to the chart. See PedigreeChartView.
  personMemberId = null,
  initialViewState,
  onPersonLockStateChange,
  // Pass-through to the sidebar (TGCLOUD-1839). See PedigreeChartView.
  onUnsavedSectionsChange
}) {
  const t = useAutoTranslation();

  const [canvasPreferences, setCanvasPreferences] = React.useState(
    DEFAULT_CANVAS_PREFERENCES
  );
  const { setSnack } = React.useContext(SnackContext);
  const {
    dispatchUpdate,
    content: {
      pedigreeSelectedUsers: users,
      settings: { preferences, pedigreeSetting }
    }
  } = React.useContext(AppContext);

  const fetchPedigreeSetting = () => {
    axios
      .get(APICONSTANTS.pedigreeSetting)
      .then((res) => {
        if (res.data) {
          dispatchUpdate({
            type: EVENTS.SET_SETTINGS,
            tab: 'pedigreeSetting',
            value: res.data
          });
        }
      })
      .catch(() => {
        setSnack({
          status: true,
          msg: t('An error occurred while fetching values'),
          severity: 'error'
        });
      });
  };

  React.useEffect(() => {
    fetchPedigreeSetting();
    localStorage.removeItem('zoom');
  }, []);

  React.useEffect(() => {
    if (preferences?.pedigreeSettings) {
      setCanvasPreferences((prev) => ({
        ...prev,
        ...preferences.pedigreeSettings
      }));
    }
  }, [preferences?.pedigreeSettings]);

  const {
    selectedUser: user,
    diagnoses = [],
    phenotypes = [],
    genotypes = []
  } = users;

  const [pedigreeConfig, setPedigreeConfig] = React.useState(null);
  // True once fetchPedigreeConfiguration has RESOLVED (success or error). The chart's
  // member mapping waits for it so a configured family never maps/lays out/paints against
  // the no-config fallback during the fetch window (spurious console warning + throwaway
  // layout computes + a visible relayout when the config landed).
  const [configLoaded, setConfigLoaded] = React.useState(false);
  // Whether that fetch FAILED (TGCLOUD-1813). A genuine `configuration: null`
  // and an error both apply the empty configuration (TGCLOUD-1804, and that
  // must stay so — a failed read must not leave the previous family's graph
  // applied), so the chart could not tell them apart. It now needs to: the
  // no-config fallback may auto-save the members it materialises from
  // placeholders only when the server said nothing is stored, never when we
  // simply could not read what is.
  const [configReadFailed, setConfigReadFailed] = React.useState(false);
  const [position, setPosition] = React.useState(new Map());
  // Phase 2: persisted within-generation order (uid -> rank). Empty until the
  // establish-order feature captures it; loaded here alongside customPositions.
  const [establishedOrder, setEstablishedOrder] = React.useState(new Map());
  // Manual-arrangement flag: the saved order is a hard LOCK only when true (user
  // drag-reordered); otherwise it just seeds orientation and layouts re-optimise.
  const [orderLocked, setOrderLocked] = React.useState(false);
  const [isMigrated, setIsMigrated] = React.useState(false);
  const [initialDateModified, setInitialDateModified] = React.useState(null);

  // Apply the WHOLE configuration state, always — never a subset.
  //
  // THE BUG THIS EXISTS FOR (TGCLOUD-1804). This used to be a chain of
  // conditional setters with no else on any of them, so a family whose read
  // came back `configuration: null`, or errored, inherited the PREVIOUS
  // family's graph, pinned positions and established order and drew itself
  // with them. `configuration: null` is not an edge case — an import that
  // skipped its config save produces exactly that. The mapping is in
  // pedigreeConfigurationState so the "nothing stored" case is pinned by a
  // test rather than only reachable by mounting this component.
  const applyPedigreeConfiguration = (configuration) => {
    const next = pedigreeConfigurationFrom(configuration);
    setPedigreeConfig(next.pedigreeConfig);
    setPosition(next.position);
    setEstablishedOrder(next.establishedOrder);
    setOrderLocked(next.orderLocked);
    setIsMigrated(next.isMigrated);
    setInitialDateModified(next.initialDateModified);
  };

  const fetchPedigreeConfiguration = () => {
    axios
      .get(APICONSTANTS.pedigreeConfig.replace(':user_id', user._id))
      .then((res) => {
        applyPedigreeConfiguration(res.data.configuration);
        setConfigReadFailed(false);
        setConfigLoaded(true);
      })
      .catch((/*err*/) => {
        setSnack({
          status: true,
          msg: t('Error: Failed to fetch pedigree Config'),
          severity: 'error'
        });
        // A failed read tells us nothing about this family, so it must not
        // leave the previous one's configuration applied either.
        applyPedigreeConfiguration(null);
        // ...but the chart must know it was a failure, not an empty answer
        // (TGCLOUD-1813): set BEFORE the gate resolves so the render that sees
        // configLoaded also sees this.
        setConfigReadFailed(true);
        // Resolve the gate even on error so the chart still renders via the fallback path.
        setConfigLoaded(true);
      });
  };

  React.useEffect(() => {
    if (preview) {
      applyPedigreeConfiguration(null);
      setConfigReadFailed(false);
      setConfigLoaded(true);
      return;
    }
    // Re-arm the gate for a refetch (a user/family change without a remount).
    setConfigLoaded(false);
    setConfigReadFailed(false);
    fetchPedigreeConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview]);

  const [labelsSet, setLabelsSet] = React.useState({
    ...PEDIGREE.defaultLabelFilters,
    ...PEDIGREE.legendLabelFilters,
    displayLabelMode: 'g-number',
    diseasesMap: diagnoses.reduce((acc, d) => {
      acc[d.id] = true;
      return acc;
    }, {}),
    phenotypes: true,
    phenotypesMap: phenotypes.reduce((acc, p) => {
      acc[p.id] = true;
      return acc;
    }, {}),
    genotypes: true,
    genotypesMap: genotypes.reduce((acc, g) => {
      acc[g.id] = true;
      return acc;
    }, {})
  });

  // Apply usergroup default displayLabelMode once per family-load.
  // User changes in the sidebar after this point are not clobbered.
  const defaultsAppliedRef = React.useRef(false);
  React.useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (!pedigreeSetting) return;
    defaultsAppliedRef.current = true;
    const next = pedigreeSetting.defaultDisplayLabelMode;
    if (next && next !== 'g-number') {
      setLabelsSet((prev) => ({ ...prev, displayLabelMode: next }));
    }
  }, [pedigreeSetting]);

  React.useEffect(() => {
    setLabelsSet((prev) => {
      const mergeIntoMap = (currentMap, items) => {
        const nextMap = { ...currentMap };
        let changed = false;
        items.forEach((item) => {
          if (!(item.id in nextMap)) {
            nextMap[item.id] = true;
            changed = true;
          }
        });
        return { nextMap, changed };
      };

      const d = mergeIntoMap(prev.diseasesMap, diagnoses);
      const p = mergeIntoMap(prev.phenotypesMap, phenotypes);
      const g = mergeIntoMap(prev.genotypesMap, genotypes);

      if (!d.changed && !p.changed && !g.changed) return prev;

      return {
        ...prev,
        diseasesMap: d.nextMap,
        phenotypesMap: p.nextMap,
        genotypesMap: g.nextMap
      };
    });
  }, [diagnoses, phenotypes, genotypes]);

  // Fetch the label presets and apply the default stack (TGCLOUD-1663). This
  // used to happen as a side effect of the sidebar's LabelConfigurations
  // mounting; the configuration now lives in an on-demand modal, so the apply
  // has to be owned here or a user's default preset would only take effect if
  // they happened to open the modal. The modal reuses `scoped` rather than
  // fetching again.
  const {
    scoped: labelPresets,
    loadError: presetLoadError,
    reload: reloadPresets,
    appliedPresetId,
    setAppliedPresetId
  } = useLabelPresets(setLabelsSet, !preview);

  // Anonymous pedigrees: clamp PII label keys at the render boundary (fail
  // closed while the settings fetch resolves), so defaults, presets and
  // persisted configurations can never draw PII node labels. The underlying
  // checkbox state is left unclamped.
  const anonymousLabels = isAnonymousOrLoading(pedigreeSetting);
  const effectiveLabelsSet = useMemo(
    () => clampLabelsSet(labelsSet, anonymousLabels),
    [labelsSet, anonymousLabels]
  );

  const options = useMemo(
    () => ({ ...canvasPreferences, labelsSet: effectiveLabelsSet }),
    [canvasPreferences, effectiveLabelsSet]
  );

  return (
    <Container>
      <Pedigree
        // KEYED ON THE FAMILY (TGCLOUD-1804). Everything below this line keeps
        // per-family state in refs and useState that a prop change does not
        // touch — memberRecords, the last-family identity ref, the canvas
        // layout, the undo history. A change of `user` was an update, not a
        // remount, so all of it survived into the next family. Keying makes a
        // family change a fresh mount, which is the only way to be sure none
        // of it carries over; it costs a remount on a switch that already
        // re-fetches and re-lays-out everything anyway.
        key={user?._id || user?.familyId || 'no-family'}
        personMemberId={personMemberId}
        initialViewState={initialViewState}
        onPersonLockStateChange={onPersonLockStateChange}
        onUnsavedSectionsChange={onUnsavedSectionsChange}
        pedigreeConfig={pedigreeConfig}
        configLoaded={configLoaded}
        configReadFailed={configReadFailed}
        isMigrated={isMigrated}
        position={position}
        initialOrder={establishedOrder}
        initialOrderLocked={orderLocked}
        options={options}
        setOptions={setCanvasPreferences}
        labelsSet={effectiveLabelsSet}
        setLabelsSet={setLabelsSet}
        labelPresets={labelPresets}
        presetLoadError={presetLoadError}
        reloadPresets={reloadPresets}
        appliedPresetId={appliedPresetId}
        setAppliedPresetId={setAppliedPresetId}
        patient={patient}
        isLocked={isLocked}
        lockResolved={lockResolved}
        isMine={isMine}
        lockState={lockState}
        requestStatus={requestStatus}
        onRequestLock={onRequestLock}
        onCancelRequest={onCancelRequest}
        onForceAcquireLock={onForceAcquireLock}
        pendingRequest={pendingRequest}
        onRespondToRequest={onRespondToRequest}
        personLockHook={personLockHook}
        initialDateModified={initialDateModified}
        onEdit={onEdit}
        preview={preview}
      />
    </Container>
  );
}
